export const getUserDetails = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}");
  } catch {
    return {};
  }
};

export const resolveSchoolId = (userDetails) => {
  const id =
    userDetails?.schooleId?._id ||
    userDetails?.schooleId ||
    userDetails?.schoolId?._id ||
    userDetails?.schoolId;
  return id != null ? String(id) : "";
};

export const isSuperAdminRole = (userDetails) =>
  String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

export const isSchooleRole = (userDetails) =>
  String(userDetails?.role || "").toUpperCase() === "SCHOOLE";

export const canUseActivityCatalog = (userDetails) =>
  isSuperAdminRole(userDetails) || isSchooleRole(userDetails);

export const normalizeId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

export const resolveSchoolLabel = (school) => {
  if (!school) return "School";
  if (typeof school === "string") return school;
  return (
    school.schooleName ||
    school.schoolName ||
    school.orgName ||
    school.name ||
    school.schoolAutoGenerateId ||
    school.email ||
    normalizeId(school._id) ||
    "School"
  );
};

export const resolveClassLabel = (cls, includeSchool = false) => {
  const className = cls?.className || "—";
  if (!includeSchool) return className;
  const schoolLabel = resolveSchoolLabel(cls?.schooleId);
  // Ignore missing labels and raw Mongo ObjectId strings (unpopulated refs).
  if (
    !schoolLabel ||
    schoolLabel === "School" ||
    /^[a-f\d]{24}$/i.test(String(schoolLabel).trim())
  ) {
    return className;
  }
  return `${className} (${schoolLabel})`;
};

/**
 * List API path segment + role query for /list/:schooleId endpoints.
 * SUPERADMIN with no filterSchoolId uses "all" (cross-tenant read).
 */
export const buildListSchoolRequest = (userDetails, filterSchoolId) => {
  const role = String(userDetails?.role || "").toUpperCase();
  if (role === "SUPERADMIN") {
    const listId = filterSchoolId ? String(filterSchoolId) : "all";
    return { listId, role: "SUPERADMIN" };
  }
  if (filterSchoolId) {
    return { listId: String(filterSchoolId), role: role || undefined };
  }
  return { listId: resolveSchoolId(userDetails), role: role || undefined };
};

export const appendRoleQuery = (url, role) => {
  if (!role) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}role=${encodeURIComponent(role)}`;
};

/** schooleId for create/update when superadmin picks a school in the catalog filter */
export const effectiveSchoolIdForWrite = (userDetails, filterSchoolId) => {
  if (filterSchoolId) {
    return String(filterSchoolId);
  }
  if (isSuperAdminRole(userDetails)) {
    return "";
  }
  return resolveSchoolId(userDetails);
};

export const resolveRowSchoolId = (row) => normalizeId(row?.schooleId);

/** SUPERADMIN catalog dialog: '' means all schools (global/default row). */
export const resolveCatalogFormSchooleId = (row) => resolveRowSchoolId(row) || '';

/** SUPERADMIN catalog save: empty selection → null (visible to all schools). */
export const resolveCatalogWriteSchooleId = (isSuperAdminScope, formSchooleId) => {
  if (!isSuperAdminScope) return formSchooleId || null;
  return formSchooleId ? String(formSchooleId) : null;
};

/** School name for rows created under a specific school; null = system/default row */
export const resolveRowSchoolLabel = (row, classList = [], schoolList = []) => {
  const sid = resolveRowSchoolId(row);
  if (!sid) return null;

  const school = (schoolList || []).find((s) => normalizeId(s._id) === sid);
  if (school) return resolveSchoolLabel(school);

  if (row?.schooleId && typeof row.schooleId === "object") {
    return resolveSchoolLabel(row.schooleId);
  }

  const classRef = row?.classId;
  if (classRef?.schooleId && typeof classRef.schooleId === "object") {
    return resolveSchoolLabel(classRef.schooleId);
  }

  const classId = normalizeId(classRef);
  const cls = (classList || []).find((c) => normalizeId(c._id) === classId);
  if (cls?.schooleId) return resolveSchoolLabel(cls.schooleId);

  return null;
};

export const isSchoolAddedRow = (row) => Boolean(resolveRowSchoolId(row));

/** Show School column in Activity Catalog tables (superadmin cross-school view) */
export const shouldShowSchoolColumn = (embedded, scope) =>
  Boolean(embedded && scope?.isSuperAdmin);

export const resolveSchoolIdForClass = (classList, classId, filterSchoolId, userDetails) => {
  if (filterSchoolId) return String(filterSchoolId);
  const cls = (classList || []).find((c) => normalizeId(c._id) === String(classId));
  const fromClass = normalizeId(cls?.schooleId);
  if (fromClass) return fromClass;
  return resolveSchoolId(userDetails);
};

/** Include global (null schooleId) rows plus rows for the selected school.
 *  Custom catalog: only rows owned by that school (no global defaults). */
export const rowMatchesSchoolScope = (row, filterSchoolId, catalogType) => {
  if (!filterSchoolId) return true;
  const rowSid = resolveRowSchoolId(row);
  if (catalogType === "custom") {
    return rowSid === String(filterSchoolId);
  }
  if (!rowSid) return true;
  return rowSid === String(filterSchoolId);
};

export const filterRowsBySchoolScope = (rows, filterSchoolId, catalogType) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!filterSchoolId) return list;
  return list.filter((row) => rowMatchesSchoolScope(row, filterSchoolId, catalogType));
};

export const buildScopedListBody = (listId, fields = {}) => {
  const body = { ...fields };
  if (listId && listId !== "all") {
    body.schooleId = String(listId);
  }
  return body;
};

/** POST body for Activity Catalog list APIs (school / class / catalog-type filters). */
export const buildCatalogListBody = (listId, { classId, catalogType, superActivityId, activityId } = {}) => {
  const body = buildScopedListBody(listId, {});
  if (classId) body.classId = String(classId);
  if (catalogType === "international" || catalogType === "indian" || catalogType === "custom") {
    body.catalogType = catalogType;
  }
  if (superActivityId) body.superActivityId = String(superActivityId);
  if (activityId) body.activityId = String(activityId);
  return body;
};

/**
 * Optional server pagination/search/field-projection on list APIs (query string).
 * - Omit `limit` → full (unpaginated) list.
 * - Pass `fields` (array or comma string) → server returns only those fields,
 *   unpopulated/lean, for lightweight dropdown/filter-option fetches.
 */
export const appendPaginationQuery = (url, { page, limit, search, fields } = {}) => {
  const params = new URLSearchParams();
  if (limit != null && limit !== "") {
    params.set("limit", String(limit));
    params.set("page", String(page ?? 0));
  }
  if (search != null && String(search).trim()) {
    params.set("search", String(search).trim());
  }
  if (fields && (Array.isArray(fields) ? fields.length : String(fields).trim())) {
    params.set("fields", Array.isArray(fields) ? fields.join(",") : String(fields));
  }
  const qs = params.toString();
  if (!qs) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
};

/** Minimal field sets for populating <Select> dropdown options (no nested populate needed). */
export const SUPER_ACTIVITY_DROPDOWN_FIELDS = [
  "_id", "superActivityName", "classId", "schooleId", "catalogType",
];
export const ACTIVITY_DROPDOWN_FIELDS = [
  "_id", "activityName", "superActivityId", "classId", "schooleId", "catalogType",
];

/** Normalize list API response (plain array or paginated envelope). */
export const parseCatalogListResponse = (res) => {
  if (Array.isArray(res)) {
    return { rows: res, total: res.length, paginated: false, pagination: null };
  }
  if (res && Array.isArray(res.data)) {
    const pagination = res.pagination || null;
    return {
      rows: res.data,
      total: pagination?.total ?? res.data.length,
      paginated: Boolean(pagination),
      pagination,
    };
  }
  return { rows: [], total: 0, paginated: false, pagination: null };
};

export const dedupeSuperActivitiesForFilter = (rows) => {
  const byKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = row?.superActivityName || "";
    const key = name ? name.toLowerCase() : String(row._id);
    if (!byKey.has(key)) byKey.set(key, row);
  });
  return Array.from(byKey.values());
};

export const filterRowsBySuperActivity = (rows, superActivityId, areaOptions = []) => {
  if (!superActivityId) return Array.isArray(rows) ? rows : [];
  const fsa = String(superActivityId);
  const selected = (areaOptions || []).find((a) => String(a._id) === fsa);
  const areaName = selected?.superActivityName;
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const sa = row?.superActivityId;
    const id = normalizeId(sa);
    if (id && id === fsa) return true;
    const name = sa?.superActivityName;
    if (areaName && name && String(name).toLowerCase() === String(areaName).toLowerCase()) {
      return true;
    }
    return false;
  });
};

/**
 * Catalog type (indian / international / custom). Legacy rows saved before this feature
 * have no catalogType and are treated as 'indian'.
 */
export const rowMatchesCatalogType = (row, catalogType) => {
  const type = row?.catalogType || "indian";
  if (catalogType === "international") return type === "international";
  if (catalogType === "custom") return type === "custom";
  return type === "indian" || (!row?.catalogType && type !== "international" && type !== "custom");
};

export const filterRowsByCatalogType = (rows, catalogType) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!catalogType) return list;
  return list.filter((row) => rowMatchesCatalogType(row, catalogType));
};

export const filterRowsByClassId = (rows, classId) => {
  if (!classId) return Array.isArray(rows) ? rows : [];
  const cid = String(classId);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const rc = row?.classId;
    const id = rc?._id != null ? rc._id : rc;
    return id != null && String(id) === cid;
  });
};

export const ACTIVITY_CATALOG_TABS = [
  {
    key: "areas",
    label: "Area Of Work",
    shortLabel: "Areas",
    description: "Top-level curriculum domains",
    step: 1,
  },
  {
    key: "activities",
    label: "List Of Activities",
    shortLabel: "Activities",
    description: "Activities within each area",
    step: 2,
  },
  {
    key: "exercises",
    label: "Exercise",
    shortLabel: "Exercises",
    description: "Individual exercises & drills",
    step: 3,
  },
];

export const tabIndexFromQuery = (search) => {
  const key = new URLSearchParams(search).get("tab");
  const idx = ACTIVITY_CATALOG_TABS.findIndex((t) => t.key === key);
  return idx >= 0 ? idx : 0;
};
